
import React, { useState } from "react";
import { Navigation } from "@/components/Navigation";
import { ScrollToTop } from "@/components/ScrollToTop";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { AdminNavigation } from "@/components/admin/AdminNavigation";
import { 
  Sparkles, 
  MessageSquare, 
  BarChart3, 
  PenSquare, 
  Save,
  BookText,
  Lightbulb
} from "lucide-react";

export default function AISettingsPage() {
  const { toast } = useToast();
  const [automaticSermonPlans, setAutomaticSermonPlans] = useState(true);
  const [journalAnalysis, setJournalAnalysis] = useState(true);
  const [analyticsGeneration, setAnalyticsGeneration] = useState(true);
  const [sermonCreativity, setSermonCreativity] = useState(75);
  const [saving, setSaving] = useState(false);

  const handleSaveSettings = () => {
    setSaving(true);
    
    // Simulate saving to the database
    setTimeout(() => {
      setSaving(false);
      toast({
        title: "Settings Saved",
        description: "Your AI feature settings have been updated successfully.",
      });
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      <ScrollToTop />
      
      <main className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            AI Features
          </h1>
          <Button onClick={handleSaveSettings} disabled={saving}>
            {saving ? (
              <>
                <span className="animate-spin mr-2">⭮</span> Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" /> Save Settings
              </>
            )}
          </Button>
        </div>

        <Card className="mb-6 bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-start">
              <Sparkles className="h-8 w-8 text-purple-500 mr-3 mt-1" />
              <div>
                <h3 className="font-medium text-purple-700 mb-1">AI-Powered Ministry</h3>
                <p className="text-sm text-purple-600">
                  Our AI features analyze journal entries, prayer requests, and community feedback to help you better understand and serve your congregation. All data is processed with complete privacy and security.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {/* Sermon Plan Generation */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xl flex items-center">
                <BookText className="h-5 w-5 mr-2 text-red-600" /> 
                Sermon Plan Generation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Automatic Sermon Plans</p>
                    <p className="text-sm text-gray-500">
                      Generate sermon plan suggestions every Friday based on congregation journal entries
                    </p>
                  </div>
                  <Switch 
                    checked={automaticSermonPlans} 
                    onCheckedChange={setAutomaticSermonPlans}
                  />
                </div>
                
                <div className="pt-2">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium">Sermon Creativity Level</p>
                    <span className="text-sm bg-gray-100 px-2 py-1 rounded-full">
                      {sermonCreativity}%
                    </span>
                  </div>
                  <Slider 
                    value={[sermonCreativity]} 
                    min={0} 
                    max={100} 
                    step={5}
                    onValueChange={(value) => setSermonCreativity(value[0])} 
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Conservative</span>
                    <span>Balanced</span>
                    <span>Creative</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Journal Analysis */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xl flex items-center">
                <MessageSquare className="h-5 w-5 mr-2 text-blue-600" /> 
                Journal Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Automatic Journal Analysis</p>
                    <p className="text-sm text-gray-500">
                      Analyze journal entries to identify common themes and spiritual needs
                    </p>
                  </div>
                  <Switch 
                    checked={journalAnalysis} 
                    onCheckedChange={setJournalAnalysis} 
                  />
                </div>
                
                <div className="flex flex-col space-y-2 bg-yellow-50 p-3 rounded-md border border-yellow-100">
                  <div className="flex items-start">
                    <Lightbulb className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Privacy Note</p>
                      <p className="text-xs text-yellow-700">
                        Journal entries are analyzed anonymously. No personal information is stored with the analysis results.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Congregation Analytics */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xl flex items-center">
                <BarChart3 className="h-5 w-5 mr-2 text-green-600" /> 
                Congregation Analytics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Generate Analytics Reports</p>
                    <p className="text-sm text-gray-500">
                      Create monthly reports on spiritual growth patterns and community engagement
                    </p>
                  </div>
                  <Switch 
                    checked={analyticsGeneration} 
                    onCheckedChange={setAnalyticsGeneration} 
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <Button variant="outline" className="flex items-center justify-center">
                    <PenSquare className="mr-2 h-4 w-4" />
                    Generate New Report
                  </Button>
                  <Button variant="outline" className="flex items-center justify-center">
                    <BarChart3 className="mr-2 h-4 w-4" />
                    View Past Reports
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <AdminNavigation />
    </div>
  );
}
